import React from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { getConfiguration } from '../apollo'
import DeliveryRateConfiguration from '../components/Configuration/DeliveryRate/DeliveryRate'
import StripeConfiguration from '../components/Configuration/Stripe/Stripe'
import PaymentGatewaysConfiguration from '../components/Configuration/PaymentGateways/PaymentGateways'
import QorConfiguration from '../components/Configuration/Qor/Qor'
import PlatformFeesConfiguration from '../components/Configuration/PlatformFees/PlatformFees'
import CurrencyConfiguration from '../components/Configuration/Currency/Currency'
import RestaurantAppReleaseConfiguration from '../components/Configuration/RestaurantAppRelease/RestaurantAppRelease'
import {
  Box, Container, Typography, CircularProgress
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'

const GET_CONFIGURATION = gql`${getConfiguration}`

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5, mt: 0.5 }}>
    {children}
  </Typography>
)

const Configuration = props => {
  const { data, error, loading } = useQuery(GET_CONFIGURATION)
  const { t } = props

  return (
    <>
      {/* Hero banner */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #334155 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SettingsIcon sx={{ color: '#94a3b8', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>Configuration</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', mt: 0.3 }}>Manage platform settings and integrations</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 6 }}>
        {error && (
          <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca', mb: 3 }}>
            <Typography sx={{ color: '#dc2626', fontSize: '0.875rem' }}>Error loading configuration</Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
        ) : data?.configuration?.isPaidVersion ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>

            {/* Fees & Commission: global, every order and gateway */}
            <Box>
              <SectionLabel>Fees & Commission</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                <PlatformFeesConfiguration />
              </Box>
            </Box>

            {/* Payments */}
            <Box>
              <SectionLabel>Payment Gateways</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                <PaymentGatewaysConfiguration
                  qorPaymentsEnabled={data.configuration.qorPaymentsEnabled}
                  stripePaymentsEnabled={data.configuration.stripePaymentsEnabled}
                />
                <StripeConfiguration
                  stripeRestaurantDashboard={data.configuration.stripeRestaurantDashboard}
                  stripeProcessingFeePercent={data.configuration.stripeProcessingFeePercent}
                  stripeProcessingFeeFixed={data.configuration.stripeProcessingFeeFixed}
                />
                <QorConfiguration />
              </Box>
            </Box>

            {/* Delivery & Currency */}
            <Box>
              <SectionLabel>Delivery & Currency</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                <DeliveryRateConfiguration
                  nearbyRadiusMiles={data.configuration.nearbyRadiusMiles}
                  pickupRadiusMiles={data.configuration.pickupRadiusMiles}
                  serverTipShare={data.configuration.serverTipShare}
                />
                <CurrencyConfiguration
                  currencyCode={data.configuration.currency}
                  currencySymbol={data.configuration.currencySymbol}
                />
              </Box>
            </Box>

            {/* Apps */}
            <Box>
              <SectionLabel>Apps</SectionLabel>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2.5 }}>
                <RestaurantAppReleaseConfiguration
                  restaurantAppLatestVersion={data.configuration.restaurantAppLatestVersion}
                  restaurantAppDownloadUrl={data.configuration.restaurantAppDownloadUrl}
                />
              </Box>
            </Box>

          </Box>
        ) : (
          <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6, p: 4, borderRadius: '20px', border: '1px solid #e2e8f0', textAlign: 'center', bgcolor: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
            <SettingsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 2 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a', mb: 1 }}>
              {t('AvailableAfterPurchasing')}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8' }}>
              Configuration options are available in the full version.
            </Typography>
          </Box>
        )}
      </Container>
    </>
  )
}

export default withTranslation()(Configuration)
